import { useState, useEffect, useRef } from 'react';
import { Alert, Button, Card, Radio, Select, Table } from 'antd';
import { showMessage } from '@nxsys/utils/message';
import {
  useCalculateUmbrellaRate,
} from '@nxsys/hooks/api/kid/use-umbrella-rate-calculator';
import { getRateConfigsApi } from '@nxsys/services/api/umbrella-rate-calculator';
import type { NmwAgeBandField } from '@nxsys/types/models/kid/umbrella-rate-calculator';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';

const marginIncrementOptions = [
  { label: '£0.25', value: 0.25 },
  { label: '£0.50', value: 0.5 },
  { label: '£1.00', value: 1 },
  { label: '£2.00', value: 2 },
  { label: '£5.00', value: 5 },
];

const taxYearOptions = [
  { label: '2027 (provisional)', value: 2027 },
  { label: '2026', value: 2026 },
  { label: '2025', value: 2025 },
];

const holidayModeOptions = [
  { label: 'Retained', value: 'RETAINED' },
  { label: 'Advanced', value: 'ROLLED_UP' },
  { label: 'Exempt', value: 'NO_HOLIDAY_PAY' },
];

const columns = [
  {
    title: 'Margin',
    dataIndex: 'margin',
    key: 'margin',
    render: (value: number) => `£${value}`,
  },
  {
    title: 'Umbrella Rate',
    dataIndex: 'minimumRate',
    key: 'minimumRate',
    render: (value: number) => `£${value?.toFixed(2)}`,
  },
];

type FormValues = {
  taxYear: number;
  hours: number;
  marginFrom: number;
  marginTo: number;
  marginIncrement: number;
  nmwAgeBand: string | null;
  pension: boolean | null;
  apprenticeshipLevy: boolean | null;
  holidayMode: string | null;
};

const initialValues: FormValues = {
  taxYear: 2026,
  hours: 40,
  marginFrom: 15,
  marginTo: 20,
  marginIncrement: 1,
  nmwAgeBand: null,
  pension: null,
  apprenticeshipLevy: null,
  holidayMode: null,
};

const UmbrellaRateCalculator = () => {
  const [form, setForm] = useState<FormValues>(initialValues);
  const [results, setResults] = useState<any[]>([]);
  const [note, setNote] = useState('');
  const [ageBandFields, setAgeBandFields] = useState<NmwAgeBandField[]>([]);

  const { run: calculate, loading: calculating } = useCalculateUmbrellaRate();
  const requestVersion = useRef(0);
  const [loadingBands, setLoadingBands] = useState(true);

  useEffect(() => {
    let active = true;
    setAgeBandFields([]);
    setLoadingBands(true);
    const load = async () => {
      try {
        // Calculator-only estimate: do not enable the new year in payroll configuration.
        const fields = form.taxYear === 2027
          ? [{ key: 'yAdult', label: '21 and above', value: 13.21, niCategory: 'A' }]
          : (await getRateConfigsApi(form.taxYear))?.nmwRates?.fields || [];
        if (!active) return;
        setAgeBandFields(fields);
        setForm((prev) => ({ ...prev, nmwAgeBand: fields[0]?.key || null }));
      } catch {
        if (active) setAgeBandFields([]);
      } finally {
        if (active) setLoadingBands(false);
      }
    };
    load();
    return () => { active = false; };
  }, [form.taxYear]);

  const clearResults = () => {
    requestVersion.current += 1;
    setResults([]);
    setNote('');
  };

  const selectedBand = ageBandFields.find((f) => f.key === form.nmwAgeBand);

  const handleCalculate = async () => {
    if (form.marginTo - form.marginFrom > 25) {
      showMessage({ type: 'error', content: 'Margin range cannot exceed £25' });
      return;
    }
    if (!form.holidayMode) {
      showMessage({ type: 'error', content: 'Please select a Holiday Mode' });
      return;
    }
    if (form.pension === null) {
      showMessage({ type: 'error', content: 'Please select a Pension option' });
      return;
    }
    if (form.apprenticeshipLevy === null) {
      showMessage({ type: 'error', content: 'Please select an Apprenticeship Levy option' });
      return;
    }
    if (form.hours <= 0) {
      showMessage({ type: 'error', content: 'Hours must be greater than 0' });
      return;
    }

    const version = ++requestVersion.current;
    setResults([]);
    setNote('');
    try {
      const res = await calculate({
        marginFrom: Number(form.marginFrom),
        marginTo: Number(form.marginTo),
        marginIncrement: form.marginIncrement,
        taxYear: form.taxYear,
        nmwAgeBand: form.nmwAgeBand || 'yAdult',
        hours: Number(form.hours),
        pension: form.pension,
        apprenticeshipLevy: form.apprenticeshipLevy,
        holidayModes: [form.holidayMode],
      });
      if (version !== requestVersion.current) return;
      if (res?.results) {
        setResults(res.results.map((item, idx) => ({ ...item, key: idx })));
        setNote(res.note || '');
      } else {
        showMessage({ type: 'error', content: 'No results returned' });
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleReset = () => {
    clearResults();
    setForm({ ...initialValues, nmwAgeBand: form.taxYear === initialValues.taxYear ? ageBandFields[0]?.key || null : null });
  };

  const updateField = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    clearResults();
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const noteText =
    note ||
    `Calculations based on NMW age band "${selectedBand?.label || '21 and above'}" (£${selectedBand?.value || '-'}/hr), NI Code ${selectedBand?.niCategory || 'A'}, ${form.hours || 40} hours.`;

  return (
    <>
    <Card
      title="UMBRELLA RATE CALCULATOR"
      extra={
        <div className="flex gap-2">
          <Button onClick={handleReset} disabled={calculating}>
            Reset
          </Button>
          <Button type="primary" onClick={handleCalculate} loading={calculating} disabled={loadingBands || !selectedBand}>
            Calculate
          </Button>
        </div>
      }
    >
      <div className="flex gap-8">
        {/* Left: Input Form */}
        <div className="w-1/2">
          <div className="grid grid-cols-2 gap-4">
            {/* Tax Year */}
            <div>
              <label className="block mb-1 font-medium">Tax Year</label>
              <Select
                className="w-full"
                options={taxYearOptions}
                value={form.taxYear}
                onChange={(val) => {
                  clearResults();
                  setAgeBandFields([]);
                  setLoadingBands(true);
                  setForm((prev) => ({ ...prev, taxYear: val, nmwAgeBand: null }));
                }}
              />
            </div>
            {/* Hours */}
            <div>
              <label className="block mb-1 font-medium">Hours</label>
              <AppRateInput
                className="w-full"
                value={form.hours}
                onChange={(val) => updateField('hours', Number(val) || 0)}
              />
            </div>
            {/* Margin From */}
            <div>
              <label className="block mb-1 font-medium">Margin From</label>
              <AppCurrencyInput
                className="w-full"
                prefix="£"
                value={form.marginFrom}
                onChange={(val) => updateField('marginFrom', Number(val) || 0)}
              />
            </div>
            {/* Margin To */}
            <div>
              <label className="block mb-1 font-medium">Margin To</label>
              <AppCurrencyInput
                className="w-full"
                prefix="£"
                value={form.marginTo}
                onChange={(val) => updateField('marginTo', Number(val) || 0)}
              />
            </div>
            {/* Margin Uplift */}
            <div>
              <label className="block mb-1 font-medium">Margin Uplift</label>
              <Select
                className="w-full"
                options={marginIncrementOptions}
                value={form.marginIncrement}
                onChange={(val) => updateField('marginIncrement', val)}
              />
            </div>
            {/* NMW Age Band */}
            <div>
              <label className="block mb-1 font-medium">NMW Age Band</label>
              <Select
                className="w-full"
                options={ageBandFields.map((f) => ({
                  label: `${f.label} (£${f.value})`,
                  value: f.key,
                }))}
                loading={loadingBands}
                disabled={loadingBands}
                value={form.nmwAgeBand}
                onChange={(val) => updateField('nmwAgeBand', val)}
              />
            </div>
            {/* Pension */}
            <div>
              <label id="calculator-pension-label" className="block mb-2 font-medium">Pension</label>
              <Radio.Group
                aria-labelledby="calculator-pension-label"
                name="calculator-pension"
                value={form.pension}
                onChange={(e) => updateField('pension', e.target.value)}
                options={[{ label: 'On', value: true }, { label: 'Off', value: false }]}
              />
            </div>
            {/* Apprenticeship Levy */}
            <div>
              <label id="calculator-levy-label" className="block mb-2 font-medium">Apprenticeship Levy</label>
              <Radio.Group
                aria-labelledby="calculator-levy-label"
                name="calculator-levy"
                value={form.apprenticeshipLevy}
                onChange={(e) => updateField('apprenticeshipLevy', e.target.value)}
                options={[{ label: 'On', value: true }, { label: 'Off', value: false }]}
              />
            </div>
            {/* Holiday Mode - full width */}
            <div className="col-span-2">
              <label id="calculator-holiday-label" className="block mb-2 font-medium">Holiday Mode</label>
              <Radio.Group
                aria-labelledby="calculator-holiday-label"
                name="calculator-holiday"
                value={form.holidayMode}
                onChange={(e) => updateField('holidayMode', e.target.value)}
                options={holidayModeOptions}
              />
            </div>
          </div>
          {form.taxYear === 2027 && (
            <Alert
              className="mt-4"
              type="warning"
              showIcon
              role="status"
              message={<strong style={{ fontSize: 18 }}>Calculations are based on the budget statement from 28/10/2026 and are subject to final approval</strong>}
              description="Pension qualifying earnings thresholds are assumed unchanged from 2026/27 and are subject to DWP confirmation."
            />
          )}
        </div>

        {/* Right: Results */}
        <div className="w-1/2">
          <div
            className="mb-3 p-2 text-sm italic rounded"
            style={{
              color: '#666',
              backgroundColor: '#f5f5f5',
              borderLeft: '3px solid #00795B',
            }}
          >
            {noteText}
          </div>
          {results.length > 0 ? (
            <div className="rate-calc-scroll-wrapper">
              <Table
                columns={columns}
                dataSource={results}
                pagination={false}
                size="small"
                bordered
                rowClassName={(_, index) =>
                  index % 2 === 0 ? 'rate-calc-row-even' : 'rate-calc-row-odd'
                }
                className="rate-calc-table"
              />
            </div>
          ) : (
            <div className="text-center text-gray-400 py-12">
              Click &quot;Calculate&quot; to see results
            </div>
          )}
        </div>
      </div>
    </Card>
    <style>{`
      /* Scroll wrapper with sticky header */
      .rate-calc-scroll-wrapper {
        max-height: calc(100vh - 450px);
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
      }
      .rate-calc-scroll-wrapper::-webkit-scrollbar {
        width: 6px;
      }
      .rate-calc-scroll-wrapper::-webkit-scrollbar-track {
        background: transparent;
      }
      .rate-calc-scroll-wrapper::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2);
        border-radius: 3px;
      }
      .rate-calc-scroll-wrapper::-webkit-scrollbar-thumb:hover {
        background-color: rgba(0, 0, 0, 0.35);
      }
      /* Allow sticky to work through Ant Design wrappers */
      .rate-calc-scroll-wrapper .ant-table,
      .rate-calc-scroll-wrapper .ant-table-container,
      .rate-calc-scroll-wrapper .ant-table-content {
        overflow: visible !important;
      }
      /* Sticky header */
      .rate-calc-table thead.ant-table-thead {
        position: sticky;
        top: 0;
        z-index: 2;
      }
      .rate-calc-table .ant-table-thead > tr > th {
        background-color: #00795B !important;
        color: #fff !important;
        font-weight: 600;
      }
      .rate-calc-row-even td {
        background-color: #ffffff !important;
      }
      .rate-calc-row-odd td {
        background-color: rgba(0, 121, 91, 0.06) !important;
      }
    `}</style>
    </>
  );
};

export default UmbrellaRateCalculator;
